<template>
  <div class="authentication">
    <section class="section">
      <div class="container">
        <h1 class="title is-2">Authentication setup page</h1>
        <b-message>
          Now you can configure the first step of adding your connection to BestAgent Marketplace.<br>
          Our goal is to make your life easier so that you don’t have to create something from scratch specifically for us.<br>
          Your API should have some kind of authentication and on this step you’re going to configure your authentication method, so that we make the connection between you and the agent on your behalf.<br><br>
          <a class="button" @click.prevent="showAllHelp = true" v-if="!showAllHelp">
            <span class="icon">
              <i class="fas fa-chevron-down"></i>
            </span>
            <span>View all steps</span>
          </a>
          <div v-if="showAllHelp" class="content">
            <p>The first step is to choose the authentication type. We currently support 3 types of authentication:</p>
            <ul>
              <li>Basic</li>
              <li>Token</li>
              <li>API Keys.</li>
            </ul>
            We will support in the future OAuth 2.0.

            <p>Basic authentication is a simple authentication scheme built into HTTP protocol where every request needs to have an Authorization header that contains the Basic word followed by a base64-encoded string username:password.</p>

            <p><strong>Token authentication</strong> is an HTTP authentication scheme which uses security tokens called bearer tokens. It also uses an Authorization header, but it will have the Bearer word instead and a cryptic string. Some APIs customize the Bearer word, so we allow you to customize the word that will go on the header.</p>

            <p><strong>API</strong> Key is a token that is sent on every request. You can customize the key name and we’ll send it as a request header.</p>

            <p>After selecting the authentication type, you can view a preview of the authentication that we’ll include on all communications with your connection.</p>

            <p>For the last step you need to provide a test URL and it’s authentication values (username/password, token or api key) so that when you click Save & Test we’ll test it automatically and check if it returns a success response. We’ll show the response we received so that you can confirm everything is ok.</p>

            <p>Recap on step by step guide:</p>

            <ul>
              <li>Select your authentication type</li>
              <li>Preview the authentication</li>
              <li>Provide a test URL</li>
            </ul>

            <a class="button" @click.prevent="showAllHelp = false" v-if="showAllHelp">
              <span class="icon">
                <i class="fas fa-chevron-up"></i>
              </span>
              <span>Hide all steps</span>
            </a>
          </div>
        </b-message>
        <h4 class="title is-4">Authentication</h4>
        <p class="subtitle is-6">Choose the authentication method of your API</p>

        <!-- Authentication Type -->
        <div class="block">
          <div class="field">
            <label class="label">Authentication Type</label>
            <div class="control">
              <div class="select">
                <select v-model="auth.authType">
                  <option value="none">No Auth</option>
                  <option value="basic">Basic Auth</option>
                  <option value="token">Token</option>
                  <option value="apikeys">API Keys</option>
                </select>
              </div>
            </div>
          </div>
        </div>

        <!-- Basic Auth -->
        <div class="block" v-if="auth.authType === 'basic'">
          <div class="field">
            <label class="label">Username</label>
            <div class="control">
              <input class="input" type="text" v-model="auth.test.val1">
            </div>
          </div>
          <div class="field">
            <label class="label">Password</label>
            <div class="control">
              <input class="input" type="password" v-model="auth.test.val2">
            </div>
          </div>
        </div>

        <!-- Token Auth -->
        <div class="block" v-if="auth.authType === 'token'">
          <div class="field">
            <label class="label">Token Type</label>
            <div class="control">
              <input class="input" type="text" v-model="auth.tokenType">
            </div>
          </div>
          <div class="field">
            <label class="label">Token value</label>
            <div class="control">
              <input class="input" type="text" v-model="auth.test.val1">
            </div>
          </div>
        </div>

        <!-- API Key Auth -->
        <div class="block" v-if="auth.authType === 'apikeys'">
          <div class="field">
            <label class="label">Key</label>
            <div class="control">
              <input class="input" type="text" v-model="auth.apikey">
            </div>
          </div>
          <div class="field">
            <label class="label">Value</label>
            <div class="control">
              <input class="input" type="text" v-model="auth.test.val1">
            </div>
          </div>
        </div>

        <!-- Preview -->
        <div class="block" v-if="auth.authType !== 'none'">
          <h5 class="title is-5">Preview of authentication header sent with every request:</h5>

          <div class="codeBlock" v-if="auth.authType === 'basic'">
<pre><code class="json">Authorization: Basic [BASE64 ENCODED CREDENTIALS]</code></pre>
          </div>
          <div class="codeBlock" v-if="auth.authType === 'token'">
<pre><code class="json">Authorization: {{auth.tokenType}} [TOKEN]</code></pre>
          </div>
          <div class="codeBlock" v-if="auth.authType === 'apikeys'">
<pre><code class="json">{{auth.apikey}}: [API KEY VALUE]</code></pre>
          </div>
        </div>

        <!-- Test URL -->
        <div class="block" v-if="auth.authType !== 'none'">
          <h5 class="title is-5">Test URL</h5>
          <p class="subtitle is-6">
            Provide a test URL to test your URL, ideally an URL that returns a simple response.
          </p>
          <div class="field has-addons">
            <p class="control">
              <span class="select">
                <select v-model="auth.test.testMethod">
                  <option value="GET">GET</option>
                  <option value="POST">POST</option>
                  <option value="PUT">PUT</option>
                  <option value="PATCH">PATCH</option>
                  <option value="DELETE">DELETE</option>
                  <option value="HEAD">HEAD</option>
                </select>
              </span>
            </p>
            <p class="control is-expanded">
              <input v-model="auth.test.testUrl" class="input" type="text" placeholder="Test Url">
            </p>
          </div>
        </div>

        <!-- Save & Test -->
        <a class="button is-success" v-if="auth.authType !== 'none'"
            @click.prevent="save">Save &amp; Test</a>
        <a class="button is-success" v-if="auth.authType === 'none'" @click.prevent="save">Save</a>

      </div>
    </section>

    <b-loading :is-full-page="true" :active="isLoading"></b-loading>
  </div>
</template>

<script>
import hljs from 'highlight.js/lib/highlight';
import bash from 'highlight.js/lib/languages/bash';
import api from '../libs/api';
import 'highlight.js/styles/github.css';

hljs.registerLanguage('bash', bash);

export default {
  name: 'authentication',
  data() {
    return {
      isLoading: false,
      showAllHelp: false,
      auth: {
        authType: 'none',
        tokenType: 'Bearer',
        apikey: 'apikey',
        test: {
          testMethod: 'GET',
          testUrl: '',
          val1: '',
          val2: '',
        },
      },
    };
  },
  async created() {
    this.isLoading = true;
    const authData = await api({
      endpoint: `${process.env.VUE_APP_API}/suppliers`,
      path: '/auth',
      method: 'GET',
    });
    if (authData) {
      this.auth = authData;
      if (this.auth.authType == 'none') {
        this.auth.tokenType = 'Bearer';
        this.auth.apikey = 'apikey';
      }
    }
    this.isLoading = false;
  },
  methods: {
    async save() {
      this.isLoading = true;
      try {
        const response = await api({
          endpoint: `${process.env.VUE_APP_API}/suppliers`,
          path: '/auth',
          method: 'POST',
          body: this.auth,
        });
        this.isLoading = false;

        if (this.auth.authType === 'none') {
          this.$router.push('/');
          return;
        }

        if (response.statusCode == 200) {
          this.$dialog.alert({
            title: 'Everything looks fine!',
            message: `Response:<br>${JSON.stringify(response.response)}`,
            type: 'is-success',
            confirmText: 'Cool!',
            hasIcon: true,
            icon: 'check-circle',
            iconPack: 'fa',
            onConfirm: () => this.$router.push('/'),
          });
        } else {
          this.$dialog.alert({
            title: 'Connection failed...',
            message: `Status Code: ${response.statusCode}<br>Response:<br>${JSON.stringify(response.response)}`,
            type: 'is-danger',
            confirmText: 'Check settings',
            hasIcon: true,
            icon: 'times-circle',
            iconPack: 'fa',
          });
        }
      } catch (err) {
        this.isLoading = false;
        console.log('Error:', err);
      }
    },
  },
};
</script>

<style lang="scss">
.authentication {
  .block {
    margin-bottom: 30px;

    .field {
      max-width: 600px;
    }
  }

  .codeBlock {
    display: inline-block;
  }
}
</style>
